import { useState } from 'react';
import type { FeedOutput, FeedRefreshMinutes, FeedStatus, Store } from '../api/types';
import { feedUrlFor, pathError, REFRESH } from '../lib/feedOutput';

interface Props {
  store: Store;
  value: FeedOutput;
  onChange: (patch: Partial<FeedOutput>) => void;
  status: FeedStatus | undefined;
  canEdit: boolean;   // org owner or admin — members see the fields read-only
}

export default function FeedSettingsForm({ store, value, onChange, status, canEdit }: Props) {
  const [showAdvanced, setShowAdvanced] = useState(false);
  const dis = !canEdit;
  const defaultPath = `${store.id}/feed.xml`;
  const draftPath = value.path ?? defaultPath;
  const url = feedUrlFor(status, draftPath);
  const pathErr = pathError(draftPath);
  const pathChanged = !!status?.published_path && status.published_path !== draftPath;

  return (
    <div className="space-y-3 text-sm">
      <label className="flex items-center gap-2 text-sm">
        <input type="checkbox" checked={value.enabled} disabled={dis}
          onChange={(e) => onChange({ enabled: e.target.checked })} data-testid="feed-output-enabled" />
        <strong className="text-ff-forest">Enable feed output</strong>
      </label>

      {!value.enabled ? (
        <p className="text-[11px] text-ff-mute">
          Turn this on to republish the store's product feed with rendered creatives swapped in.
        </p>
      ) : (
        <div className="space-y-3">
          <div>
            <label className="ff-label">Feed URL</label>
            <div className="flex gap-2 items-center">
              <code className="flex-1 truncate text-[11px] font-mono bg-white/60 border border-ff-sand/40 px-2 py-1 rounded"
                data-testid="feed-output-url">{url ?? 'Loading…'}</code>
              <button type="button" disabled={!url} onClick={() => url && navigator.clipboard.writeText(url)}
                className="text-[11px] text-ff-forest hover:underline disabled:opacity-40"
                data-testid="feed-output-copy-url">Copy</button>
            </div>
          </div>

          <div>
            <label className="ff-label" htmlFor="feed-output-path">Path</label>
            <input id="feed-output-path" type="text" value={draftPath} disabled={dis}
              onChange={(e) => onChange({ path: e.target.value })}
              className="ff-input text-sm font-mono" data-testid="feed-output-path" />
            {pathErr && <p className="mt-1 text-[11px] text-ff-danger" data-testid="feed-output-path-error">{pathErr}</p>}
            {!pathErr && pathChanged && (
              <p className="mt-1 text-[11px] text-ff-warning" data-testid="feed-output-path-warning">
                Currently published at <code>{status?.published_path}</code>. After the next publish the old file is
                deleted — repoint Meta and Google to the new URL first.
              </p>
            )}
          </div>

          <div>
            <label className="ff-label" htmlFor="feed-output-source-url">Source feed URL</label>
            <input id="feed-output-source-url" type="url" value={value.source_url ?? ''} disabled={dis}
              onChange={(e) => onChange({ source_url: e.target.value || null })}
              placeholder={store.source.feed_url ?? 'https://…/feed.xml'}
              className="ff-input text-sm font-mono" data-testid="feed-output-source-url" />
            <p className="mt-1 text-[11px] text-ff-mute">Leave empty to use the store's feed URL.</p>
          </div>

          <div className="grid gap-3 sm:grid-cols-3">
            <div>
              <label className="ff-label" htmlFor="feed-output-missing-render">Products without a render</label>
              <select id="feed-output-missing-render" value={value.missing_render} disabled={dis}
                onChange={(e) => onChange({ missing_render: e.target.value as FeedOutput['missing_render'] })}
                className="ff-input text-sm" data-testid="feed-output-missing-render">
                <option value="drop">Leave out of the feed</option>
                <option value="keep_source">Keep with the original image</option>
              </select>
            </div>
            <div>
              <label className="ff-label" htmlFor="feed-output-additional-images">Additional images</label>
              <select id="feed-output-additional-images" value={value.additional_images} disabled={dis}
                onChange={(e) => onChange({ additional_images: e.target.value as FeedOutput['additional_images'] })}
                className="ff-input text-sm" data-testid="feed-output-additional-images">
                <option value="keep">Keep</option>
                <option value="drop">Remove</option>
              </select>
            </div>
            <div>
              <label className="ff-label" htmlFor="feed-output-refresh">Refresh</label>
              <select id="feed-output-refresh" value={value.refresh_minutes} disabled={dis}
                onChange={(e) => onChange({ refresh_minutes: Number(e.target.value) as FeedRefreshMinutes })}
                className="ff-input text-sm" data-testid="feed-output-refresh">
                {REFRESH.map((o) => <option key={o.value} value={o.value}>{o.label}</option>)}
              </select>
              <p className="mt-1 text-[11px] text-ff-mute">
                How often we re-check the source feed. Renders are picked up automatically after every render run.
              </p>
            </div>
          </div>

          <button type="button" onClick={() => setShowAdvanced((v) => !v)}
            className="text-[11px] text-ff-forest hover:underline" data-testid="feed-output-advanced">
            {showAdvanced ? 'Hide advanced' : 'Advanced'}
          </button>
          {showAdvanced && (
            <div>
              <label className="ff-label" htmlFor="feed-output-image-tag">Image tag</label>
              <input id="feed-output-image-tag" type="text" value={value.image_tag} disabled={dis}
                onChange={(e) => onChange({ image_tag: e.target.value })}
                className="ff-input text-sm font-mono w-60" data-testid="feed-output-image-tag" />
              <p className="mt-1 text-[11px] text-ff-mute">Local tag name of the image element; <code>g:</code> and bare both match.</p>
            </div>
          )}
        </div>
      )}
    </div>
  );
}
